/**
 * Institution Documents Section - Manages institution link proof documents
 * Files are selected but NOT uploaded until parent component calls uploadPendingFiles()
 */
import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemSecondaryAction,
    Chip,
    IconButton,
    CircularProgress,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { intl, fetchData, fetchUrl, HTTP_STATUS, Notifications } from "dmsbasereact";
import { DocumentUploadSlot } from "../../../../components/DocumentUploadSlot/DocumentUploadSlot";
import { downloadFile } from "../../../../utils/fileDownload";
import { UserDocumentTypeEnum } from "../../../../constants/UserDocumentRequirements";

export const InstitutionDocumentsSection = forwardRef(({
    selectedInstitutions = [],
    institutionNameMap = {},
    orgBranchToInstitutionMap = {},
    onDocumentsChange = null
}, ref) => {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedInstitutionPk, setSelectedInstitutionPk] = useState("");
    const [pendingFiles, setPendingFiles] = useState({}); // { institutionPk: File }

    const fetchDocuments = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetchData("/account/documents", "GET", true);
            if (response.status === HTTP_STATUS.OK_200) {
                // Filter valid institution-related document types
                // Use organization_branch_pk for frontend mapping (backend returns both)
                const validTypes = [
                    UserDocumentTypeEnum.INSTITUTION_LINK_PROOF,
                    UserDocumentTypeEnum.CRT_CERTIFICATE,
                    UserDocumentTypeEnum.EMPLOYMENT_CONTRACT,
                    UserDocumentTypeEnum.PROFESSIONAL_ID,
                    UserDocumentTypeEnum.RESPONSIBILITY_TERM,
                ];
                const institutionDocs = (response.data?.data || [])
                    .filter(doc => validTypes.includes(doc.document_type))
                    .map(doc => ({
                        ...doc,
                        // Use organization_branch_pk for frontend (what user selected)
                        institution_pk: doc.organization_branch_pk || doc.institution_pk
                    }));
                setDocuments(institutionDocs);
                if (onDocumentsChange) {
                    onDocumentsChange(institutionDocs);
                }
            }
        } catch (err) {
            console.error("Failed to fetch documents:", err);
        } finally {
            setLoading(false);
        }
    }, [onDocumentsChange]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    const handleFileSelect = (docName, file) => {
        if (file && selectedInstitutionPk) {
            setPendingFiles(prev => ({
                ...prev,
                [selectedInstitutionPk]: file
            }));
            setSelectedInstitutionPk("");
        }
    };

    const handleRemovePendingFile = (institutionPk) => {
        setPendingFiles(prev => {
            const newPending = { ...prev };
            delete newPending[institutionPk];
            return newPending;
        });
    };

    const uploadPendingFiles = async () => {
        const uploads = Object.entries(pendingFiles);
        if (uploads.length === 0) return [];

        const uploadedDocs = [];
        for (const [orgBranchPk, file] of uploads) {
            try {
                // Convert organization_branch_pk to institution.pk
                const institutionPk = orgBranchToInstitutionMap[orgBranchPk];

                if (!institutionPk) {
                    console.error(`No institution.pk found for org_branch_pk=${orgBranchPk}`);
                    throw new Error(`Institution mapping not found for organization branch ${orgBranchPk}`);
                }

                const formData = new FormData();
                formData.append("file", file);

                const response = await fetchUrl(
                    `/account/documents/upload?document_type=1&institution_pk=${institutionPk}`,
                    "POST",
                    true,
                    formData,
                    false,
                    null,
                    null,
                    null,
                    'multipart/form-data',
                    false
                );

                if (response.status === HTTP_STATUS.CREATED_201 || response.status === HTTP_STATUS.OK_200) {
                    uploadedDocs.push(response.data?.data);
                } else {
                    throw new Error(response.data?.detail || "Upload failed");
                }
            } catch (err) {
                console.error(`Failed to upload document for org_branch ${orgBranchPk}:`, err);
                throw err; // Propagate error to parent
            }
        }

        // Clear pending files after successful upload
        setPendingFiles({});
        await fetchDocuments(); // Refresh document list
        return uploadedDocs;
    };

    // Expose uploadPendingFiles to parent via ref
    useImperativeHandle(ref, () => ({
        uploadPendingFiles,
        hasPendingFiles: () => Object.keys(pendingFiles).length > 0
    }));

    const handleDownload = async (filePk) => {
        try {
            await downloadFile(filePk);
        } catch (err) {
            Notifications.toast(
                intl.get("DocumentDownloadError").d("Error downloading document"),
                Notifications.TYPE_ERROR
            );
        }
    };

    const getInstitutionName = (institutionPk) => {
        return institutionNameMap[institutionPk] || `Institution ${institutionPk}`;
    };

    // Get institutions that don't have documents (uploaded or pending)
    const institutionsWithoutDocs = selectedInstitutions.filter(instPk => {
        const hasUploaded = documents.some(doc => doc.institution_pk === instPk);
        const hasPending = !!pendingFiles[instPk];
        return !hasUploaded && !hasPending;
    });

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
                {intl.get("InstitutionLinkDocuments").d("Institution Link Documents")}
            </Typography>

            <Alert severity="info" sx={{ mb: 2 }}>
                {intl.get("InstitutionLinkDocumentsInfo").d(
                    "Upload proof documents for each institution you want to link to. Each institution requires a separate document."
                )}
            </Alert>

            {/* Upload section */}
            <Box sx={{ mb: 3, p: 2, bgcolor: "grey.50", borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    {intl.get("AddDocument").d("Add Document")}
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
                    <FormControl size="small" sx={{ minWidth: 250 }}>
                        <InputLabel>{intl.get("SelectInstitution").d("Select Institution")}</InputLabel>
                        <Select
                            value={selectedInstitutionPk}
                            label={intl.get("SelectInstitution").d("Select Institution")}
                            onChange={(e) => setSelectedInstitutionPk(e.target.value)}
                        >
                            {selectedInstitutions.map((instPk) => {
                                const hasDoc = documents.some(doc => doc.institution_pk === instPk);
                                const hasPending = !!pendingFiles[instPk];
                                const disabled = hasDoc || hasPending;
                                return (
                                    <MenuItem key={instPk} value={instPk} disabled={disabled}>
                                        {getInstitutionName(instPk)}
                                        {(hasDoc || hasPending) && ` (${intl.get("HasDocument").d("Has document")})`}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                    <Box sx={{ flex: 1 }}>
                        <DocumentUploadSlot
                            docName={intl.get("SelectFile").d("Select File")}
                            file={null}
                            onFileSelect={handleFileSelect}
                            disabled={!selectedInstitutionPk}
                        />
                    </Box>
                </Box>
            </Box>

            {/* Pending files (not uploaded yet) */}
            {Object.keys(pendingFiles).length > 0 && (
                <>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {intl.get("PendingDocuments").d("Pending Documents")} ({Object.keys(pendingFiles).length})
                    </Typography>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {intl.get("DocumentsWillBeUploadedOnSave").d(
                            "These documents will be uploaded when you click Save"
                        )}
                    </Alert>
                    <List disablePadding sx={{ mb: 2 }}>
                        {Object.entries(pendingFiles).map(([instPk, file]) => (
                            <ListItem
                                key={instPk}
                                sx={{
                                    bgcolor: "warning.lighter",
                                    borderRadius: 1,
                                    mb: 1,
                                    border: "1px solid",
                                    borderColor: "warning.main",
                                }}
                            >
                                <ListItemIcon>
                                    <AttachFileIcon color="warning" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={getInstitutionName(parseInt(instPk))}
                                    secondary={
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                            <Chip
                                                size="small"
                                                label={intl.get("PendingUpload").d("Pending upload")}
                                                color="warning"
                                                sx={{ width: "fit-content" }}
                                            />
                                            <Typography variant="caption" color="text.secondary">
                                                {file.name} ({(file.size / 1024).toFixed(1)} KB)
                                            </Typography>
                                        </Box>
                                    }
                                />
                                <ListItemSecondaryAction>
                                    <Tooltip title={intl.get("Remove").d("Remove")}>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleRemovePendingFile(instPk)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                </>
            )}

            {/* Uploaded documents */}
            {documents.length > 0 && (
                <>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {intl.get("UploadedDocuments").d("Uploaded Documents")} ({documents.length})
                    </Typography>
                    <List disablePadding>
                        {documents.map((doc) => (
                            <ListItem
                                key={doc.pk}
                                sx={{
                                    bgcolor: "background.paper",
                                    borderRadius: 1,
                                    mb: 1,
                                    border: "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                <ListItemIcon>
                                    <DescriptionIcon color="action" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={getInstitutionName(doc.institution_pk)}
                                    secondary={
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                            <Chip
                                                size="small"
                                                label={intl.get("DocumentUploaded").d("Document uploaded")}
                                                color="success"
                                                sx={{ width: "fit-content" }}
                                            />
                                            {doc.created_on && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {intl.get("UploadedOn").d("Uploaded on")}: {new Date(doc.created_on).toLocaleDateString('pt-BR')}
                                                </Typography>
                                            )}
                                        </Box>
                                    }
                                />
                                <ListItemSecondaryAction>
                                    <Tooltip title={intl.get("Download").d("Download")}>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDownload(doc.file_pk)}
                                        >
                                            <DownloadIcon />
                                        </IconButton>
                                    </Tooltip>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                </>
            )}

            {/* Warning if missing documents */}
            {institutionsWithoutDocs.length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    {intl.get("MissingDocumentsForInstitutions").d(
                        "The following institutions need documents:"
                    )} {institutionsWithoutDocs.map(getInstitutionName).join(", ")}
                </Alert>
            )}

            {documents.length === 0 && Object.keys(pendingFiles).length === 0 && (
                <Alert severity="info">
                    {intl.get("NoInstitutionDocumentsUploaded").d("No institution link documents uploaded yet")}
                </Alert>
            )}
        </Box>
    );
});
